import type { Goals, MealSlot } from "@shared/types";

/** Fixed share of the daily calorie goal allotted to each meal. Sums to 1. */
const SPLIT: Record<MealSlot, number> = {
  breakfast: 0.25, lunch: 0.35, dinner: 0.30, snack: 0.10,
};

/** Per-meal kcal targets from the daily goal. A custom split on Goals (all
 *  four percents set, summing to 100 — the endpoint enforces it) replaces the
 *  preset; otherwise the fixed shares above apply. */
export function mealTargets(
  dailyCalories: number,
  goals?: Pick<Goals, "split_breakfast" | "split_lunch" | "split_dinner" | "split_snack"> | null,
): Record<MealSlot, number> {
  const custom = goals
    && goals.split_breakfast != null && goals.split_lunch != null
    && goals.split_dinner != null && goals.split_snack != null
    ? {
        breakfast: goals.split_breakfast / 100,
        lunch: goals.split_lunch / 100,
        dinner: goals.split_dinner / 100,
        snack: goals.split_snack / 100,
      }
    : SPLIT;
  return {
    breakfast: Math.round(dailyCalories * custom.breakfast),
    lunch:     Math.round(dailyCalories * custom.lunch),
    dinner:    Math.round(dailyCalories * custom.dinner),
    snack:     Math.round(dailyCalories * custom.snack),
  };
}

/** Carbs/fat targets for the macro bars. When a goal macro is unset, fall back to
 *  a 45%-carb / 30%-fat-of-calories split (4 kcal/g carbs, 9 kcal/g fat). Protein
 *  always uses its own goal. Sugar target is derived from WHO ~10% of energy. */
export function macroTargets(goals: Goals): { carbs_g: number; protein_g: number; fat_g: number; sugar_g: number; fiber_g: number } {
  return {
    protein_g: goals.protein_g,
    carbs_g: goals.carbs_g ?? Math.round((goals.daily_calories * 0.45) / 4),
    fat_g:   goals.fat_g   ?? Math.round((goals.daily_calories * 0.30) / 9),
    sugar_g: Math.round((goals.daily_calories * 0.10) / 4),
    fiber_g: goals.fiber_target_g ?? 30,
  };
}
